import { connect } from 'react-redux';
import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

import { reorderNodes, deleteNode } from '../../actionCreators';
import { sameParent } from '../../formBuilderHelpers';
import DeleteComponentModal from './DeleteComponentModal';
import RenderComponent from './RenderComponent';

const Types = {
  COMPONENT: 'component',
  SECTION: 'section'
};

/**
 * Wrapper component that adds drag and drop functionality to a form component
 */
const DragDropComponent = (props) => {
  const {
    node,
    active,
    setActiveComponent,
    unSetActiveComponent,
    formRoot,
    isCurrentNodeValid,
    disabled,
    sectionKey,
    putChildren,
    deleteNode
  } = props;

  const [{ isDragging }, drag] = useDrag({
    type: Types.COMPONENT,
    item: { node },
    canDrag: !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: Types.COMPONENT,
    canDrop: () => true,
    hover: (item, monitor) => {
      const componentDragged = item.node;
      const componentDraggedTo = node;
      const parent = sameParent(
        componentDraggedTo,
        componentDragged,
        formRoot
      );
      if (componentDragged === componentDraggedTo || !parent) {
        // If it is the same component, or the components have different parents
        return;
      } // Don't reorder parent's child order if it is the same element & only same section dnd.
      putChildren(componentDraggedTo, componentDragged, formRoot);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const deleteComponent = (possibleParentNode, node) => {
    deleteNode(possibleParentNode, node);
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const fieldProps = {
    connectDragSource: drag,
    node,
    active,
    setActiveComponent,
    unSetActiveComponent,
    openDeleteModal,
    isCurrentNodeValid,
    disabled,
    sectionKey
  };

  const modalProps = {
    formRoot,
    node,
    deleteComponent,
    open: deleteModalOpen,
    closeModal: closeDeleteModal
  };

  return (
    <div className="form-builder-field-wrapper">
      <DeleteComponentModal {...modalProps} />
      <div ref={drop}>
        <RenderComponent {...fieldProps} />
      </div>
    </div>
  );
};

DragDropComponent.propTypes = {
  node: PropTypes.object,
  active: PropTypes.bool,
  setActiveComponent: PropTypes.func,
  unSetActiveComponent: PropTypes.func,
  formRoot: PropTypes.object,
  isCurrentNodeValid: PropTypes.bool,
  disabled: PropTypes.bool,
  sectionKey: PropTypes.string,
  putChildren: PropTypes.func,
  deleteNode: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
  return {
    formRoot: state.formBuilders[ownProps.node.formKey]
  };
};

const mapDispatchToProps = (dispatch) => ({
  putChildren: bindActionCreators(reorderNodes, dispatch),
  deleteNode: bindActionCreators(deleteNode, dispatch)
});

const Component = connect(
  mapStateToProps,
  mapDispatchToProps
)(DragDropComponent);

export default Component;
export { DragDropComponent as ComponentWithoutWrappers };
